// One-time post-login prompt to enable follow-up reminders.
// Never auto-re-prompts once the therapist enables, declines, or dismisses it.
import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import {
  pushSupported, pushDeclined, markDeclined, enablePush,
  notificationPermission, isSubscribed,
} from "../lib/push";

export function NotificationPrompt() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const openSheet = () => setOpen(true);
    window.addEventListener("pstr-open-notifications", openSheet);

    (async () => {
      if (!pushSupported()) return;
      if (notificationPermission() !== "default") return; // already granted or denied
      if (pushDeclined()) return;
      if (await isSubscribed()) return;
      if (!cancelled) {
        const t = setTimeout(() => setOpen(true), 3500);
        window.__pstrNotifTimer = t;
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(window.__pstrNotifTimer);
      window.removeEventListener("pstr-open-notifications", openSheet);
    };
  }, []);

  if (!open) return null;

  const notNow = () => {
    markDeclined();
    setOpen(false);
  };

  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Reminders on — we'll nudge you about unfinished follow-up");
      setOpen(false);
    } catch (e) {
      toast.error(e.message || "Couldn't enable notifications");
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center print:hidden" data-testid="notification-prompt-sheet">
      <div className="absolute inset-0 bg-ink-900/40 backdrop-blur-sm" onClick={notNow} aria-hidden="true" />
      <div className="relative w-full sm:max-w-md bg-paper-50 rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <button
          type="button"
          data-testid="notification-close-btn"
          onClick={notNow}
          aria-label="Close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-paper-100 inline-flex items-center justify-center text-ink-500"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-sage-500 flex items-center justify-center shrink-0">
            <Bell className="w-6 h-6 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h2 className="font-heading text-xl text-ink-900">Never leave follow-up hanging</h2>
            <p className="text-sm text-ink-500">Same-day reminders, straight to your phone.</p>
          </div>
        </div>
        <p className="text-sm text-ink-700 mb-5">
          Finished treatment but didn&apos;t get to the client&apos;s recommendations yet? Posturra will remind you before the day ends — no client details ever appear on the notification.
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            data-testid="notification-enable-btn"
            onClick={enable}
            disabled={busy}
            className="flex-1 min-h-[48px] rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium transition-all active:scale-95 inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Bell className="w-4 h-4" strokeWidth={1.5} /> {busy ? "Enabling…" : "Enable reminders"}
          </button>
          <button
            type="button"
            data-testid="notification-not-now-btn"
            onClick={notNow}
            className="min-h-[48px] px-6 rounded-full border border-paper-200 hover:bg-paper-100 text-ink-700 font-medium transition-all"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
